import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import SEOHead from "@/components/SEOHead";
import ReviewTile, { Stars, GoogleG, TrustpilotStar } from "@/components/ReviewTile";
import reviewsData from "@/data/reviews.json";
import analytics from "@/utils/analytics";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};
const stagger = { visible: { transition: { staggerChildren: 0.05 } } };
// Fast grid-tile stagger — tiles should cascade in quickly on load,
// not wait per-card like a hero block.
const gridStagger = {
  visible: { transition: { staggerChildren: 0.02, delayChildren: 0.15 } },
};
const tileFadeUp = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.35, ease: "easeOut" } },
};

// ─── page ───────────────────────────────────────────────────────────────

export default function ReviewsPage() {
  const { reviews, totals, averageRating, generatedAt } = reviewsData;
  const [sourceFilter, setSourceFilter] = useState("all"); // "all" | "google" | "trustpilot"

  const lastUpdated = useMemo(() => {
    const d = generatedAt ? new Date(generatedAt) : new Date();
    return Number.isNaN(d.getTime())
      ? ""
      : d.toLocaleDateString("en-GB", { month: "long", year: "numeric" });
  }, [generatedAt]);

  const visibleReviews = useMemo(() => {
    if (sourceFilter === "all") return reviews;
    return reviews.filter((r) => r.source === sourceFilter);
  }, [reviews, sourceFilter]);

  const googleUrl = reviews.find((r) => r.source === "google")?.profileUrl;
  const trustpilotUrl = reviews.find((r) => r.source === "trustpilot")?.profileUrl;

  const FILTERS = [
    { key: "all", label: "All reviews", count: totals.total },
    { key: "google", label: "Google", count: totals.google },
    { key: "trustpilot", label: "Trustpilot", count: totals.trustpilot },
  ];

  return (
    <div data-testid="reviews-page">
      <SEOHead
        title={`${totals.total}+ Customer Reviews on Google & Trustpilot`}
        description={`Read ${totals.total}+ real reviews from CanDo Payments customers across Google and Trustpilot. Rated ${averageRating}/5 by UK businesses for transparent pricing and dependable merchant services.`}
        path="/reviews"
      />

      {/* Hero ─────────────────────────────────────────────────────────── */}
      <section className="hero-gradient py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial="hidden" animate="visible" variants={stagger}>
            <motion.p
              variants={fadeUp}
              className="text-xs font-bold uppercase tracking-[0.25em] text-brand-aqua mb-3"
            >
              Customer Reviews
            </motion.p>
            <motion.h1
              variants={fadeUp}
              className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-brand-black tracking-tight mb-6"
            >
              {`Reviewed and trusted by ${totals.total}+ UK businesses`}
            </motion.h1>
            <motion.p
              variants={fadeUp}
              className="text-lg text-brand-grey max-w-2xl mx-auto mb-8"
            >
              Every review below is verified and published on Google or Trustpilot — no cherry-picking, no edits. Click any card to see it on the source platform.
            </motion.p>

            {/* Summary pills */}
            <motion.div
              variants={fadeUp}
              className="flex flex-wrap items-center justify-center gap-3 sm:gap-4"
            >
              <div
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-brand-light-grey shadow-sm"
                data-testid="reviews-average-pill"
              >
                <Stars rating={Math.round(averageRating)} size={14} />
                <span className="text-sm font-bold text-brand-black">
                  {`${averageRating.toFixed(2)} / 5`}
                </span>
                <span className="text-xs text-brand-grey">average</span>
              </div>

              {googleUrl && (
                <a
                  href={googleUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-brand-light-grey hover:border-brand-aqua/50 shadow-sm transition-colors"
                  data-testid="reviews-google-pill"
                >
                  <GoogleG size={14} />
                  <span className="text-sm font-semibold text-brand-black">
                    {`${totals.google} on Google`}
                  </span>
                  <ArrowUpRight size={14} className="text-brand-grey" />
                </a>
              )}

              {trustpilotUrl && (
                <a
                  href={trustpilotUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-brand-light-grey hover:border-brand-aqua/50 shadow-sm transition-colors"
                  data-testid="reviews-trustpilot-pill"
                >
                  <TrustpilotStar size={14} />
                  <span className="text-sm font-semibold text-brand-black">
                    {`${totals.trustpilot} on Trustpilot`}
                  </span>
                  <ArrowUpRight size={14} className="text-brand-grey" />
                </a>
              )}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Filter chips ─────────────────────────────────────────────────── */}
      <section className="pt-10 pb-4 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div
            className="flex flex-wrap items-center justify-center gap-2 sm:gap-3"
            role="tablist"
            aria-label="Filter reviews by source"
            data-testid="reviews-filter-chips"
          >
            {FILTERS.map((f) => {
              const isActive = sourceFilter === f.key;
              return (
                <button
                  key={f.key}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => {
                    setSourceFilter(f.key);
                    analytics.reviewFilterSelected(f.key);
                  }}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-colors border ${
                    isActive
                      ? "bg-brand-black text-white border-brand-black"
                      : "bg-white text-brand-black border-brand-light-grey hover:border-brand-aqua/50"
                  }`}
                  data-testid={`reviews-filter-${f.key}`}
                >
                  {f.key === "google" && <GoogleG size={14} />}
                  {f.key === "trustpilot" && <TrustpilotStar size={14} />}
                  <span>{f.label}</span>
                  <span
                    className={`text-xs px-1.5 py-0.5 rounded-full ${
                      isActive ? "bg-white/15 text-white" : "bg-brand-light-grey/60 text-brand-grey"
                    }`}
                  >
                    {f.count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Reviews grid ─────────────────────────────────────────────────── */}
      <section className="pt-6 pb-14 md:pb-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            key={sourceFilter}
            initial="hidden"
            animate="visible"
            variants={gridStagger}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 items-start"
            data-testid="reviews-grid"
          >
            {visibleReviews.map((r) => (
              <ReviewTile
                key={`${r.source}::${r.author}::${(r.text || "").slice(0, 40)}::${sourceFilter}`}
                review={r}
                motionVariant={tileFadeUp}
                testId={`review-card-${r.source}`}
                onSourceClick={() =>
                  analytics.reviewCardClicked(r.source, {
                    rating: r.rating,
                    author: r.author,
                  })
                }
              />
            ))}
          </motion.div>

          {lastUpdated && (
            <p className="mt-10 text-xs text-brand-grey text-center">
              {`Showing ${visibleReviews.length} of ${totals.total} reviews · dataset last refreshed ${lastUpdated}.`}
            </p>
          )}
        </div>
      </section>

      {/* CTA ──────────────────────────────────────────────────────────── */}
      <section className="bg-brand-black text-white py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-4">
            Join them — see what you'd save
          </h2>
          <p className="text-white/70 mb-8 max-w-2xl mx-auto">
            Our customers average 36% savings on card processing. Upload a recent statement and we'll show you yours — no obligation, no sales pitch.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/statement-review"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-brand-aqua text-brand-black font-semibold hover:bg-brand-aqua/90 transition-colors"
              data-testid="reviews-cta-statement"
            >
              Get a free statement review
              <ArrowRight size={16} />
            </Link>
            <Link
              to="/book"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/20 text-white hover:border-white/60 transition-colors"
              data-testid="reviews-cta-book"
            >
              Book a 20-min call
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
